import { useCallback, useEffect, useRef, useState } from 'react';
import { ChatService } from '../../../services/chatService';
import {
	clearConversation,
	readConversation,
	writeConversation,
} from './chatStorage';

const chatService = new ChatService();

const NoAnswerFallback =
	'The assistant did not return an answer. Please try again.';

let messageCounter = 0;

const createMessage = (message) => ({
	id: `${Date.now()}-${messageCounter++}`,
	...message,
});

// randomUUID only exists in secure contexts, so plain-http environments fall
// back to a v4-shaped id built from getRandomValues.
const createConversationId = () => {
	if (typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID();
	}
	return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
		(
			c ^
			(crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
		).toString(16)
	);
};

// 400 carries a ModelState-shaped body, 505 an empty one, and the interceptor
// swallows 401/403 into an undefined response.
export const formatChatError = (error) => {
	const data = error?.response?.data;

	if (typeof data === 'string' && data.trim()) {
		return data;
	}

	if (data?.errors && typeof data.errors === 'object') {
		const messages = Object.values(data.errors)
			.reduce((all, value) => all.concat(value), [])
			.filter((value) => typeof value === 'string' && value.trim());
		if (messages.length) {
			return messages.join('\n');
		}
	}

	if (data?.title) {
		return data.title;
	}

	if (!error?.response) {
		return 'The assistant is unreachable right now. Check your connection and try again.';
	}

	return 'Something went wrong while answering. Please try again.';
};

export const useChatConversation = () => {
	// Read storage once per mount rather than on every render.
	const restoredRef = useRef(null);
	if (!restoredRef.current) {
		restoredRef.current = readConversation();
	}
	const restored = restoredRef.current;

	const [messages, setMessages] = useState(restored.messages);
	const [isSending, setIsSending] = useState(false);

	const abortRef = useRef(null);
	const sendingRef = useRef(false);
	const mountedRef = useRef(true);
	// Bumped by reset, so an answer landing after New chat is discarded.
	const generationRef = useRef(0);
	// Replaced by reset, so every new chat is a new conversation on the API.
	const conversationIdRef = useRef(
		restored.conversationId || createConversationId()
	);

	useEffect(() => {
		if (!messages.length) {
			clearConversation();
			return;
		}
		writeConversation({ conversationId: conversationIdRef.current, messages });
	}, [messages]);

	// Abort on unmount, so a resolved promise cannot set state afterwards.
	useEffect(() => {
		return () => {
			mountedRef.current = false;
			abortRef.current?.abort();
		};
	}, []);

	const appendMessage = useCallback((message) => {
		if (!mountedRef.current) {
			return;
		}
		setMessages((current) => current.concat(createMessage(message)));
	}, []);

	const requestAnswer = useCallback(
		async (prompt) => {
			const generation = generationRef.current;
			const controller = new AbortController();
			abortRef.current = controller;
			sendingRef.current = true;
			setIsSending(true);

			try {
				const resp = await chatService.sendMessage(
					{ message: prompt, conversationId: conversationIdRef.current },
					controller.signal
				);

				if (generationRef.current !== generation) {
					return;
				}

				if (!resp?.data) {
					// 401 and 403 never reject; the interceptor returns undefined.
					throw new Error('empty-response');
				}

				appendMessage({
					role: 'assistant',
					content: resp.data.answer || NoAnswerFallback,
					report: resp.data.report || null,
				});
			} catch (error) {
				if (generationRef.current !== generation) {
					return;
				}

				if (controller.signal.aborted) {
					appendMessage({
						role: 'assistant',
						content: 'Response stopped.',
						isNotice: true,
					});
				} else {
					appendMessage({
						role: 'assistant',
						content:
							error?.message === 'empty-response'
								? NoAnswerFallback
								: formatChatError(error),
						isError: true,
						prompt: prompt,
					});
				}
			} finally {
				abortRef.current = null;
				sendingRef.current = false;
				if (mountedRef.current) {
					setIsSending(false);
				}
			}
		},
		[appendMessage]
	);

	const send = useCallback(
		(text) => {
			const prompt = text.trim();
			if (!prompt || sendingRef.current) {
				return;
			}
			appendMessage({ role: 'user', content: prompt });
			requestAnswer(prompt);
		},
		[appendMessage, requestAnswer]
	);

	// Replaces the failed answer in place: the question is already in the thread.
	const retry = useCallback(
		(messageId, prompt) => {
			if (sendingRef.current) {
				return;
			}
			setMessages((current) =>
				current.filter((message) => message.id !== messageId)
			);
			requestAnswer(prompt);
		},
		[requestAnswer]
	);

	const stop = useCallback(() => {
		abortRef.current?.abort();
	}, []);

	const reset = useCallback(() => {
		generationRef.current += 1;
		abortRef.current?.abort();
		conversationIdRef.current = createConversationId();
		setMessages([]);
	}, []);

	return { messages, isSending, send, retry, stop, reset };
};
